import { LockOpen, KeyRound, Key, Radio, Smartphone, Copy, SearchX, Cpu, Cog, ScanEye, Wrench, Truck, CircleHelp, Car, Bus, Home, Building2, ParkingCircle, MapPin, Zap, Sun, CalendarClock, Phone, MessageSquare, Mail, Ban, Lock, Power } from "lucide-react";
import { Tile, Field, inputCls } from "./Tile";
import { KeyPhotos } from "./KeyPhotos";
import { Moto, Van } from "@/components/site/VehicleTypes";
import { BrandMark } from "@/components/site/BrandMark";
import { EURO_BRANDS, FEATURED_NAMES, MAKES } from "@/lib/data";
import { W_SERVICES, W_VEHICLES, W_KEYS, W_PLACES, W_WHEN, W_TIMES, W_CONTACT } from "@/lib/locales/wizard";

const SVC_ICONS = [LockOpen, KeyRound, Radio, Key, Smartphone, Copy, SearchX, Cpu, Cog, ScanEye, Wrench, Truck, CircleHelp];
const VEH_ICONS = [Car, Moto, Van, Truck, Bus];
const KEY_ICONS = [Copy, SearchX, Ban, Lock, Power, CircleHelp];
const PLACE_ICONS = [Home, Building2, ParkingCircle, MapPin, CircleHelp];
const WHEN_ICONS = [Zap, Sun, CalendarClock];
const WA = () => <span className="brand-mask h-8 w-8" style={{ WebkitMaskImage: "url(/brands/whatsapp.svg)", maskImage: "url(/brands/whatsapp.svg)" }} />;
const CONTACT_ICONS = [Phone, MessageSquare, WA, Mail];
const FEATURED = EURO_BRANDS.filter((b) => FEATURED_NAMES.includes(b.name));
const YEARS = Array.from({ length: 2027 - 1980 }, (_, i) => String(2026 - i)).concat("Older");
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Grid = ({ cols = "grid-cols-2 sm:grid-cols-3", children }) => <div className={`grid gap-3 ${cols}`}>{children}</div>;

const Choice = ({ options, icons, labels, value, onPick, prefix, cols }) => (
  <Grid cols={cols}>
    {options.map((o, i) => (
      <Tile key={o} testId={`${prefix}-${slug(o)}`} icon={icons[i]} label={labels[o] || o} active={value === o} onClick={() => onPick(o)} />
    ))}
  </Grid>
);

export const StepService = ({ f, set, w, next }) => (
  <Choice options={W_SERVICES} icons={SVC_ICONS} labels={w.services} value={f.service} prefix="wiz-service" cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" onPick={(v) => { set("service", v); next(); }} />
);

export const StepVehicle = ({ f, set, w }) => (
  <div className="space-y-8">
    <Choice options={W_VEHICLES} icons={VEH_ICONS} labels={w.vehicles} value={f.vehicle_type} prefix="wiz-vehicle" cols="grid-cols-2 sm:grid-cols-5" onPick={(v) => set("vehicle_type", v)} />
    <div>
      <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{w.makeLabel}</div>
      <Grid cols="grid-cols-4 sm:grid-cols-8">
        {FEATURED.map((b) => (
          <button key={b.name} type="button" data-testid={`wiz-make-${slug(b.name)}`} title={b.name} onClick={() => set("make", b.name)} className={`grid aspect-square place-items-center border transition-colors duration-300 ${f.make === b.name ? "border-brand bg-brand text-cream" : "border-white/15 bg-ink text-bone hover:border-brand"}`}>
            <BrandMark brand={b} className="h-10 w-12" />
          </button>
        ))}
      </Grid>
      <select data-testid="wiz-make-select" value={MAKES.includes(f.make) ? f.make : ""} onChange={(e) => set("make", e.target.value)} className={`${inputCls} mt-3 cursor-pointer`}>
        <option value="">{w.otherMake}</option>
        {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  </div>
);

export const StepDetails = ({ f, set, w }) => (
  <div className="grid gap-5 sm:grid-cols-2">
    <Field label={w.model}><input data-testid="wiz-model-input" className={inputCls} value={f.model} onChange={(e) => set("model", e.target.value)} placeholder={w.modelPh} autoComplete="off" /></Field>
    <Field label={w.year}>
      <select data-testid="wiz-year-select" className={`${inputCls} cursor-pointer`} value={f.year} onChange={(e) => set("year", e.target.value)}>
        <option value="">{w.yearPh}</option>
        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </Field>
    <div className="sm:col-span-2"><Field label={w.vin}><input data-testid="wiz-vin-input" className={`${inputCls} uppercase`} value={f.vin} onChange={(e) => set("vin", e.target.value.slice(0, 17))} placeholder={w.vinPh} autoComplete="off" /></Field></div>
  </div>
);

const NO_KEY_SERVICES = ["Car Unlock", "Lock Repair & Maintenance", "Computer / ECU Programming", "Transmission / TCM Programming", "ADAS Calibration"];
export const isKeyService = (s) => !!s && !NO_KEY_SERVICES.includes(s);

export const StepKey = ({ f, set, w }) => (
  <div className="space-y-6">
    <Choice options={W_KEYS} icons={KEY_ICONS} labels={w.keys} value={f.key_status} prefix="wiz-key" onPick={(v) => set("key_status", v)} />
    {isKeyService(f.service) && <KeyPhotos photos={f.key_photos} onChange={(v) => set("key_photos", v)} w={w} />}
  </div>
);

export const StepPlace = ({ f, set, w }) => (
  <div className="space-y-6">
    <Choice options={W_PLACES} icons={PLACE_ICONS} labels={w.places} value={f.place} prefix="wiz-place" cols="grid-cols-2 sm:grid-cols-5" onPick={(v) => set("place", v)} />
    <Field label={w.location}><input data-testid="wiz-location-input" className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} placeholder={w.locationPh} autoComplete="street-address" /></Field>
  </div>
);

export const StepWhen = ({ f, set, w }) => (
  <div className="space-y-6">
    <Choice options={W_WHEN} icons={WHEN_ICONS} labels={w.when} value={f.urgency} prefix="wiz-when" cols="grid-cols-1 sm:grid-cols-3" onPick={(v) => set("urgency", v)} />
    {f.urgency === "Schedule a time" && (
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={w.date}><input data-testid="wiz-date-input" type="date" min={new Date().toISOString().slice(0, 10)} className={inputCls} value={f.schedule_date} onChange={(e) => set("schedule_date", e.target.value)} /></Field>
        <Field label={w.time}>
          <select data-testid="wiz-time-select" className={`${inputCls} cursor-pointer`} value={f.schedule_time} onChange={(e) => set("schedule_time", e.target.value)}>
            <option value="">{w.timePh}</option>
            {W_TIMES.map((o) => <option key={o} value={o}>{w.times[o]}</option>)}
          </select>
        </Field>
      </div>
    )}
  </div>
);

export const StepContact = ({ f, set, w }) => (
  <div className="grid gap-5 sm:grid-cols-2">
    <Field label={w.name}><input data-testid="wiz-name-input" name="name" autoComplete="name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
    <Field label={w.phone}><input data-testid="wiz-phone-input" name="tel" autoComplete="tel" type="tel" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(617) 000-0000" /></Field>
    <div className="sm:col-span-2"><Field label={w.email}><input data-testid="wiz-email-input" name="email" autoComplete="email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></Field></div>
    <div className="sm:col-span-2">
      <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{w.prefer}</div>
      <Choice options={W_CONTACT} icons={CONTACT_ICONS} labels={w.contact} value={f.preferred_contact} prefix="wiz-contact" cols="grid-cols-2 sm:grid-cols-4" onPick={(v) => set("preferred_contact", v)} />
    </div>
    <div className="sm:col-span-2"><Field label={w.notes}><textarea data-testid="wiz-notes-input" rows={3} className={`${inputCls} resize-none`} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder={w.notesPh} /></Field></div>
  </div>
);

export const STEPS = [StepService, StepVehicle, StepDetails, StepKey, StepPlace, StepWhen, StepContact];

export const stepValid = (i, f) => [
  !!f.service,
  !!f.vehicle_type && !!f.make,
  true,
  !!f.key_status,
  f.location.trim().length >= 3,
  !!f.urgency && (f.urgency !== "Schedule a time" || (!!f.schedule_date && !!f.schedule_time)),
  f.name.trim().length > 0 && f.phone.trim().length >= 7,
][i];
